import { forwardRef } from "react";
import FormikFieldError from "../FormikFieldError";
import LabelWithGuideline from "../LabelWithGuideline";
import { getInputWrapperClasses } from "./inputStyles";
import {
	CompactSelect,
	type CompactSelectOption,
	type CompactSelectProps,
} from "./CompactSelect";

export type { CompactSelectOption };

export interface CompactSelectWithLabelProps
	extends Omit<CompactSelectProps, "name"> {
	/** Wrapper className (applied to the outer block). */
	className?: string;
	/** Inline error text rendered beneath the select. */
	errorText?: string;
	/** Optional guideline shown via the info-tooltip beside the label. */
	guideLine?: string;
	/** Field label rendered above the select. */
	label?: string;
	/** `name`/`id` used to associate the label with the select. */
	name?: string;
	/** Hide the label entirely (still rendered for a11y if `label` is set). */
	noLabel?: boolean;
	/** Show the red asterisk on the label. */
	required?: boolean;
}

/**
 * Compact native `<select>` with a label and optional guideline tooltip.
 *
 * Mirrors `SelectWithLabel`'s API surface (label / guideLine / required /
 * errorText) but renders the lightweight `CompactSelect` native element
 * rather than react-select — appropriate for tight inline forms (column
 * editor popovers, spreadsheet cell editors, etc.) where the dropdown is
 * a secondary control rather than a primary picker.
 */
export const CompactSelectWithLabel = forwardRef<
	HTMLSelectElement,
	CompactSelectWithLabelProps
>(
	(
		{
			className = "",
			errorText,
			guideLine,
			hasError = false,
			label,
			name,
			noLabel,
			required,
			...selectProps
		},
		ref
	) => {
		const hasInputError = !!errorText || hasError;

		return (
			<div className={getInputWrapperClasses(hasInputError, false, className)}>
				{!noLabel && (
					<LabelWithGuideline
						guideLine={guideLine}
						htmlFor={name}
						label={label}
						required={required}
					/>
				)}
				<CompactSelect
					ref={ref}
					id={name}
					name={name}
					hasError={hasInputError}
					{...selectProps}
				/>
				<FormikFieldError text={errorText} />
			</div>
		);
	}
);

CompactSelectWithLabel.displayName = "CompactSelectWithLabel";
